import { Check, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { SelectField, TextField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { IUser } from '@/MODELS/user.model'

import { api } from '@/services/api.service'
import {
    ageOptions,
    birthYearOptions,
    communicationLevelOptions,
    currentDiagnoseStepOptions,
    diagnosisOptions,
    genderOptions,
    guardianRoleOptions,
    mainConcernOptions,
    monthOptions,
    previousActivitiesOptions,
    SERVICES_CONSENT_LABEL,
    servicesReceivedOptions,
    supportNeedsOptions,
    userGoalsAsAParentOptions,
    userGoalsForChildOptions,
} from '@/utils/options'

import InteractiveLoading from '@/components/InteractiveLoading'
import { NameToolTip } from '@/components/OnboardingToolTip'

interface BaseFormProps {
    formValues: any
    setFormValues: (updater: (prev: any) => any) => void
    f: (fieldName: string) => any
}

interface WelcomeSectionProps extends BaseFormProps {}

interface ChildInfoSectionProps extends BaseFormProps {}

interface MainConcernsSectionProps extends BaseFormProps {}

interface JourneyStageSectionProps extends BaseFormProps {}

interface UserGoalsSectionProps extends BaseFormProps {}

interface DiagnosisInfoSectionProps extends BaseFormProps {}

interface CommunicationLevelSectionProps extends BaseFormProps {}

interface SupportNeedsSectionProps extends BaseFormProps {}

interface ServicesReceivedSectionProps extends BaseFormProps {}

interface FinalConfirmationSectionProps extends BaseFormProps {
    submissionStatus: string
    user: IUser
    // When the family's sponsoring org requires provider review, their roadmap is
    // prepared by the provider rather than generated for them on the spot — so we
    // skip the long "worth the wait" AI loading screen while onboarding submits.
    reviewRequired?: boolean
}

export function WelcomeSection({ formValues, setFormValues, f }: WelcomeSectionProps) {
    return (
        <section className='space-y-6' key='step-1'>
            <div className='space-y-4 text-center'>
                <h1 className='text-3xl font-bold text-white sm:text-4xl'>Welcome to BrightPath</h1>
                <p className='text-lg text-white sm:text-xl'>Your personalized autism care journey starts here</p>
            </div>

            <div className='p-6 space-y-5 border bg-white/20 backdrop-blur-sm rounded-2xl border-white/30'>
                <div className='mb-2 text-center'>
                    <h2 className='mb-2 text-2xl font-semibold text-white'>Tell us about you</h2>
                    <p className='text-base text-white'>Help us personalize your experience</p>
                </div>

                <div>
                    <label className='block mb-2 text-base font-medium text-white'>What should we call you?</label>
                    <NameToolTip>
                        <TextField
                            {...f('name')}
                            autoComplete='given-name'
                            name='name'
                            label={false}
                            placeholder='Mom, Emily, or anything you like'
                            className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        />
                    </NameToolTip>
                    <p className='mt-1 text-sm text-white/75'>Optional</p>
                </div>

                <div>
                    <label className='block mb-3 text-base font-medium text-white'>
                        What's your role? <span className='mt-1 text-sm text-white/80'>(Optional)</span>
                    </label>
                    <div className='grid grid-cols-1 gap-2'>
                        {guardianRoleOptions.map((option) => (
                            <button
                                key={option.value}
                                type='button'
                                onClick={() =>
                                    setFormValues((prev) => ({
                                        ...prev,
                                        userInfo: {
                                            ...prev.userInfo,
                                            guardianRole: option.value,
                                        },
                                    }))
                                }
                                className={`px-3 py-2.5 rounded-lg border transition-all duration-200 font-medium text-base ${
                                    formValues?.userInfo?.guardianRole === option.value
                                        ? 'bg-white text-cyan-600 border-white shadow-md'
                                        : 'bg-white/25 text-white border-white/40 hover:bg-white/35'
                                }`}
                            >
                                {option.text}
                            </button>
                        ))}
                    </div>
                </div>

                {formValues?.userInfo?.guardianRole === 'other' && (
                    <div>
                        <label className='block mb-2 text-base font-medium text-white'>Please specify your role</label>
                        <TextField
                            {...f('userInfo.guardianRoleOther')}
                            label={false}
                            placeholder='Please describe your role'
                            className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        />
                    </div>
                )}
            </div>
        </section>
    )
}

export function ChildInfoSection({ formValues, setFormValues, f }: ChildInfoSectionProps) {
    return (
        <section className='space-y-6' key='step-2'>
            <div className='text-center'>
                <h2 className='mb-3 text-2xl font-bold text-white sm:text-3xl'>About your child</h2>
            </div>

            <div className='p-6 space-y-5 border bg-white/20 backdrop-blur-sm rounded-2xl border-white/30'>
                <div>
                    <label className='block mb-2 text-base font-medium text-white'>Child's name</label>
                    <NameToolTip>
                        <TextField
                            {...f('userInfo.childName')}
                            className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                            placeholder='Jamie, Lily, or anything you like (optional)'
                            label={false}
                        />
                    </NameToolTip>
                </div>

                <div>
                    <label className='block mb-3 text-base font-medium text-white'>
                        Child's gender <span className='mt-1 text-sm text-white/80'>(Optional)</span>
                    </label>
                    <div className='grid grid-cols-1 gap-2'>
                        {genderOptions.map((option) => (
                            <button
                                key={option.value}
                                type='button'
                                onClick={() =>
                                    setFormValues((prev) => ({
                                        ...prev,
                                        userInfo: {
                                            ...prev.userInfo,
                                            childGender: option.value,
                                        },
                                    }))
                                }
                                className={`px-3 py-2.5 rounded-lg border transition-all duration-200 font-medium text-base ${
                                    formValues?.userInfo?.childGender === option.value
                                        ? 'bg-white text-cyan-600 border-white shadow-md'
                                        : 'bg-white/25 text-white border-white/40 hover:bg-white/35'
                                }`}
                            >
                                {option.text}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className='block mb-3 text-base font-medium text-white'>
                        Child's date of birth <span className='text-yellow-200'>(Required)</span>
                    </label>
                    <p className='mb-2 text-sm text-white/80'>Month and year only</p>
                    {/* Hidden required input enforces both fields are set before form can advance */}
                    <input
                        type='text'
                        required
                        value={
                            formValues?.userInfo?.childBirthMonth && formValues?.userInfo?.childBirthYear
                                ? 'set'
                                : ''
                        }
                        onChange={() => {}}
                        tabIndex={-1}
                        aria-hidden='true'
                        className='absolute opacity-0 h-0 w-0 pointer-events-none'
                    />
                    <div className='grid grid-cols-2 gap-2'>
                        <select
                            value={String(formValues?.userInfo?.childBirthMonth ?? '')}
                            onChange={(e) => {
                                const month = e.target.value ? parseInt(e.target.value, 10) : undefined
                                setFormValues((prev) => ({
                                    ...prev,
                                    userInfo: {
                                        ...prev.userInfo,
                                        childBirthMonth: month,
                                    },
                                }))
                            }}
                            className='w-full px-4 py-3 text-base text-gray-800 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        >
                            <option value=''>Month</option>
                            {monthOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </select>
                        <select
                            value={String(formValues?.userInfo?.childBirthYear ?? '')}
                            onChange={(e) => {
                                const year = e.target.value ? parseInt(e.target.value, 10) : undefined
                                setFormValues((prev) => ({
                                    ...prev,
                                    userInfo: {
                                        ...prev.userInfo,
                                        childBirthYear: year,
                                    },
                                }))
                            }}
                            className='w-full px-4 py-3 text-base text-gray-800 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        >
                            <option value=''>Year</option>
                            {birthYearOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </section>
    )
}

export function ServicesReceivedSection({ formValues, setFormValues, f }: ServicesReceivedSectionProps) {
    const consent = !!formValues?.userInfo?.servicesReceivedConsent
    const selectedServices: string[] = formValues?.userInfo?.servicesReceived ?? []

    function toggleService(value: string) {
        setFormValues((prev) => {
            const current: string[] = prev.userInfo?.servicesReceived ?? []
            const next = current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value]
            return {
                ...prev,
                userInfo: {
                    ...prev.userInfo,
                    servicesReceived: next,
                },
            }
        })
    }

    function setConsent(checked: boolean) {
        setFormValues((prev) => ({
            ...prev,
            userInfo: {
                ...prev.userInfo,
                servicesReceivedConsent: checked,
                servicesReceived: checked ? prev.userInfo?.servicesReceived ?? [] : [],
            },
        }))
    }

    return (
        <section className='space-y-6' key='step-services-received'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                    Services your child is currently receiving
                </h2>
                <p className='text-base text-white/90 drop-shadow-md'>
                    Optional. We use this to skip ahead on milestones you've already started.
                </p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                <label className='flex items-start gap-3 px-3 py-3 rounded-lg cursor-pointer bg-white/15 border border-white/30'>
                    <input
                        type='checkbox'
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className='mt-1 w-5 h-5 rounded text-cyan-600 border-white/60 focus:ring-yellow-400'
                    />
                    <span className='text-sm text-white leading-snug'>{SERVICES_CONSENT_LABEL}</span>
                </label>

                {consent && (
                    <div className='space-y-2'>
                        <p className='text-sm text-white/80'>Select all that apply</p>
                        {servicesReceivedOptions.map((option) => {
                            const isSelected = selectedServices.includes(option.value)
                            return (
                                <button
                                    key={option.value}
                                    type='button'
                                    onClick={() => toggleService(option.value)}
                                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                                        isSelected
                                            ? 'bg-white text-cyan-600 border-white shadow-md'
                                            : 'bg-white/25 text-white border-white/40 hover:bg-white/35'
                                    }`}
                                >
                                    {option.text}
                                </button>
                            )
                        })}
                    </div>
                )}
            </div>
        </section>
    )
}

export function MainConcernsSection({ formValues, setFormValues, f }: MainConcernsSectionProps) {
    return (
        <section className='space-y-6' key='step-3'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>Main concerns</h2>
                <p className='text-base text-white/90 drop-shadow-md'>
                    Select all that apply <span className='text-yellow-200 text-sm font-normal'>(Required)</span>
                </p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {mainConcernOptions.map((option) => {
                    const isSelected = formValues?.userInfo?.mainConcerns?.includes(option.value)
                    return (
                        <button
                            key={option.value}
                            type='button'
                            onClick={() => {
                                const current = formValues?.userInfo?.mainConcerns || []
                                const updated = isSelected
                                    ? current.filter((item) => item !== option.value)
                                    : [...current, option.value]
                                setFormValues((prev) => ({
                                    ...prev,
                                    userInfo: {
                                        ...prev.userInfo,
                                        mainConcerns: updated,
                                    },
                                }))
                            }}
                            className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                                isSelected
                                    ? 'bg-white text-cyan-600 border-white shadow-md'
                                    : 'bg-white/20 text-white border-white/40 hover:bg-white/35'
                            }`}
                        >
                            {option.text}
                        </button>
                    )
                })}

                {formValues?.userInfo?.mainConcerns?.includes('other') && (
                    <TextField
                        {...f('userInfo.mainConcernsOther')}
                        className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        placeholder='Please specify your other concern'
                        label={false}
                    />
                )}
            </div>
        </section>
    )
}

export function JourneyStageSection({ formValues, setFormValues, f }: JourneyStageSectionProps) {
    return (
        <section className='space-y-6' key='step-4'>
            <div className='text-center'>
                <h2 className='mb-2 text-xl font-bold text-white sm:text-3xl '>Where are you on your journey?</h2>
                <p className='text-base text-white/90 drop-shadow-md'>This helps us provide relevant guidance</p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {/* Hidden required input enforces selection before form can advance */}
                <input
                    type='text'
                    required
                    value={formValues?.userInfo?.currentDiagnoseStep || ''}
                    onChange={() => {}}
                    tabIndex={-1}
                    aria-hidden='true'
                    className='absolute opacity-0 h-0 w-0 pointer-events-none'
                />
                {currentDiagnoseStepOptions.map((option) => (
                    <button
                        key={option.value}
                        type='button'
                        onClick={() =>
                            setFormValues((prev) => ({
                                ...prev,
                                userInfo: {
                                    ...prev.userInfo,
                                    currentDiagnoseStep: option.value,
                                },
                            }))
                        }
                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                            formValues?.userInfo?.currentDiagnoseStep === option.value
                                ? 'bg-white text-cyan-600 border-white shadow-md'
                                : 'bg-white/10 text-white border-white/40 hover:bg-white/35'
                        }`}
                    >
                        {option.text}
                    </button>
                ))}

                {formValues?.userInfo?.currentDiagnoseStep === 'pre-diagnosis-waitlist' && (
                    <div className='p-4 border bg-white/15 backdrop-blur-sm rounded-xl border-white/30'>
                        <label className='block mb-2 text-base font-medium text-white'>
                            When did you sign up for the waitlist?
                        </label>
                        <TextField
                            {...f('userInfo.waitlistSignUpDate')}
                            label={false}
                            placeholder='e.g., January 2024, 6 months ago, Last fall'
                            className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        />
                        <p className='mt-1 text-sm text-white/75'>You can be approximate</p>
                    </div>
                )}
            </div>
        </section>
    )
}

export function DiagnosisInfoSection({ formValues, setFormValues, f }: DiagnosisInfoSectionProps) {
    const selectedDiagnoses: string[] = formValues?.userInfo?.diagnosisInformation ?? []

    function toggleDiagnosis(value: string) {
        setFormValues((prev) => {
            const current: string[] = prev.userInfo?.diagnosisInformation ?? []
            const next = current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value]
            return {
                ...prev,
                userInfo: {
                    ...prev.userInfo,
                    diagnosisInformation: next,
                },
            }
        })
    }

    return (
        <section className='space-y-6' key='step-5'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>Diagnosis information</h2>
                <p className='text-base text-white/90 drop-shadow-md'>Help us understand your child's diagnosis</p>
                <p className='text-sm text-white/70 drop-shadow-md'>Select all that apply</p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {diagnosisOptions.map((option) => (
                    <button
                        key={option.value}
                        type='button'
                        onClick={() => toggleDiagnosis(option.value)}
                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                            selectedDiagnoses.includes(option.value)
                                ? 'bg-white text-cyan-600 border-white shadow-md'
                                : 'bg-white/25 text-white border-white/40 hover:bg-white/35'
                        }`}
                    >
                        {option.text}
                    </button>
                ))}

                {selectedDiagnoses.includes('other') && (
                    <div className='p-4 border bg-white/15 backdrop-blur-sm rounded-xl border-white/30'>
                        <label className='block mb-2 text-base font-medium text-white'>
                            Please specify the diagnosis
                        </label>
                        <TextField
                            {...f('userInfo.diagnosisInformationOther')}
                            label={false}
                            placeholder='Please describe the specific diagnosis'
                            className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        />
                    </div>
                )}
            </div>
        </section>
    )
}
export function CommunicationLevelSection({ formValues, setFormValues, f }: CommunicationLevelSectionProps) {
    return (
        <section className='space-y-6' key='step-communication'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                    How does your child primarily communicate right now?
                </h2>
                <p className='text-base text-white/90 drop-shadow-md'>This helps us personalize support strategies</p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {communicationLevelOptions.map((option) => (
                    <button
                        key={option.value}
                        type='button'
                        onClick={() =>
                            setFormValues((prev) => ({
                                ...prev,
                                userInfo: {
                                    ...prev.userInfo,
                                    communicationLevel: option.value,
                                },
                            }))
                        }
                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                            formValues?.userInfo?.communicationLevel === option.value
                                ? 'bg-white text-cyan-600 border-white shadow-md'
                                : 'bg-white/10 text-white border-white/40 hover:bg-white/35'
                        }`}
                    >
                        {option.text}
                    </button>
                ))}
            </div>
        </section>
    )
}

export function SupportNeedsSection({ formValues, setFormValues, f }: SupportNeedsSectionProps) {
    return (
        <section className='space-y-6' key='step-support-needs'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                    How much support does your child currently need in daily routines?
                </h2>
                <p className='text-base text-white/90 drop-shadow-md'>
                    Think about eating, dressing, transitions, or play
                </p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {supportNeedsOptions.map((option) => (
                    <button
                        key={option.value}
                        type='button'
                        onClick={() =>
                            setFormValues((prev) => ({
                                ...prev,
                                userInfo: {
                                    ...prev.userInfo,
                                    supportNeeds: option.value,
                                },
                            }))
                        }
                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                            formValues?.userInfo?.supportNeeds === option.value
                                ? 'bg-white text-cyan-600 border-white shadow-md'
                                : 'bg-white/10 text-white border-white/40 hover:bg-white/35'
                        }`}
                    >
                        {option.text}
                    </button>
                ))}
            </div>
        </section>
    )
}

export function UserGoalsSection({ formValues, setFormValues, f }: UserGoalsSectionProps) {
    return (
        <section className='space-y-6' key='step-goals'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                    What are your goals for your child?
                </h2>
                <p className='text-base text-white/90 drop-shadow-md'>
                    Select all that apply — this will help us personalize your journey and checklists
                </p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {userGoalsForChildOptions.map((option) => {
                    const isSelected = formValues?.userInfo?.userGoalsForChild?.includes(option.value)
                    return (
                        <button
                            key={option.value}
                            type='button'
                            onClick={() => {
                                const current = formValues?.userInfo?.userGoalsForChild || []
                                const updated = isSelected
                                    ? current.filter((item) => item !== option.value)
                                    : [...current, option.value]
                                setFormValues((prev) => ({
                                    ...prev,
                                    userInfo: {
                                        ...prev.userInfo,
                                        userGoalsForChild: updated,
                                    },
                                }))
                            }}
                            className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                                isSelected
                                    ? 'bg-white text-cyan-600 border-white shadow-md'
                                    : 'bg-white/25 text-white border-white/40 hover:bg-white/35'
                            }`}
                        >
                            {option.text}
                        </button>
                    )
                })}

                {formValues?.userInfo?.userGoalsForChild?.includes('other') && (
                    <TextField
                        {...f('userInfo.userGoalsOther')}
                        className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        placeholder='Please don’t include personal details here'
                        label={false}
                    />
                )}
            </div>
        </section>
    )
}

export function UserGoalsSectionCaregivers({ formValues, setFormValues, f }: UserGoalsSectionProps) {
    return (
        <section className='space-y-6' key='step--caregiver-goals'>
            <div className='text-center'>
                <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                    What are your goals as a parent or caregiver?
                </h2>
                <p className='text-base text-white/90 drop-shadow-md'>
                    Select all that apply — this will help us support you and your family’s journey
                </p>
            </div>

            <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30'>
                {userGoalsAsAParentOptions.map((option) => {
                    const isSelected = formValues?.userInfo?.userGoalsCareGiver?.includes(option.value)
                    return (
                        <button
                            key={option.value}
                            type='button'
                            onClick={() => {
                                const current = formValues?.userInfo?.userGoalsCareGiver || []
                                const updated = isSelected
                                    ? current.filter((item) => item !== option.value)
                                    : [...current, option.value]
                                setFormValues((prev) => ({
                                    ...prev,
                                    userInfo: {
                                        ...prev.userInfo,
                                        userGoalsCareGiver: updated,
                                    },
                                }))
                            }}
                            className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 font-medium text-left text-base ${
                                isSelected
                                    ? 'bg-white text-cyan-600 border-white shadow-md'
                                    : 'bg-white/25 text-white border-white/40 hover:bg-white/35'
                            }`}
                        >
                            {option.text}
                        </button>
                    )
                })}

                {formValues?.userInfo?.userGoalsCareGiver?.includes('other') && (
                    <TextField
                        {...f('userInfo.userGoalsOther')}
                        className='w-full px-4 py-3 text-base text-gray-800 placeholder-gray-500 transition-all duration-200 border-0 rounded-xl bg-white/90 backdrop-blur-sm focus:ring-2 focus:ring-yellow-400 focus:bg-white'
                        placeholder='Please don’t include personal details here'
                        label={false}
                    />
                )}
            </div>
        </section>
    )
}

export function FinalConfirmationSection({ formValues, submissionStatus, user, reviewRequired }: FinalConfirmationSectionProps) {
    return (
        <section className='space-y-6' key='step-final'>
            {submissionStatus === 'pending' ? (
                reviewRequired ? (
                    // Provider prepares the roadmap — no AI wait for the family.
                    <div className='flex flex-col items-center justify-center h-64 text-center px-4'>
                        <div className='w-10 h-10 mb-4 border-4 rounded-full border-white/40 border-t-white animate-spin' />
                        <p className='text-lg font-semibold text-white drop-shadow'>Finishing up…</p>
                        <p className='mt-1 text-base text-white/85'>Saving your information.</p>
                    </div>
                ) : (
                    <div className='flex flex-col items-center justify-center h-64'>
                        <div className='flex flex-col items-center justify-center'>
                            <InteractiveLoading showProgress={false} />
                        </div>
                    </div>
                )
            ) : (
                <>
                    <div className='text-center'>
                        <h2 className='mb-2 text-2xl font-bold text-white sm:text-3xl drop-shadow-lg'>
                            Ready to start!
                        </h2>
                        <p className='mb-6 text-base text-white/90 drop-shadow-md'>Confirm your details below</p>
                    </div>

                    <div className='p-6 space-y-5 border bg-white/10 backdrop-blur-sm rounded-2xl border-white/30 '>
                        <div className='space-y-3'>
                            <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                <p className='mb-1 text-sm font-medium text-white'>Your Name</p>
                                <p className='text-lg text-white'>{formValues?.name || 'Not provided'}</p>
                            </div>

                            {formValues?.userInfo?.guardianRole && (
                                <div className='p-3 rounded-lg bg-white/10 border border-white/20 shadow-sm'>
                                    <p className='mb-1 text-sm font-medium text-white'>Your Role</p>
                                    <p className='text-base text-white'>
                                        {formValues.userInfo.guardianRole === 'other'
                                            ? formValues.userInfo.guardianRoleOther || 'Other'
                                            : guardianRoleOptions.find(
                                                  (opt) => opt.value === formValues.userInfo.guardianRole
                                              )?.text || 'Not provided'}
                                    </p>
                                </div>
                            )}

                            {user && (
                                <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                    <p className='mb-1 text-sm font-medium text-yellow-200'>Email</p>
                                    <p className='text-lg text-white'>{user?.email}</p>
                                </div>
                            )}

                            {formValues?.userInfo && (
                                <>
                                    {/* Child Info (Step 2) */}
                                    {formValues.userInfo.childName && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Child's Name</p>
                                            <p className='text-base text-white'>{formValues.userInfo.childName}</p>
                                        </div>
                                    )}

                                    {formValues.userInfo.childGender && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Child's Gender</p>
                                            <p className='text-lg text-white'>
                                                {genderOptions.find(
                                                    (opt) => opt.value === formValues.userInfo.childGender
                                                )?.text || 'Not provided'}
                                            </p>
                                        </div>
                                    )}

                                    {(formValues.userInfo.childBirthMonth && formValues.userInfo.childBirthYear) ? (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Child's Date of Birth</p>
                                            <p className='text-base text-white'>
                                                {monthOptions.find((m) => m.value === String(formValues.userInfo.childBirthMonth))?.text}{' '}
                                                {formValues.userInfo.childBirthYear}
                                            </p>
                                        </div>
                                    ) : formValues.userInfo.childAge && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Child's Age</p>
                                            <p className='text-base text-white'>
                                                {ageOptions.find((opt) => opt.value === formValues.userInfo.childAge)
                                                    ?.text || 'Not provided'}
                                            </p>
                                        </div>
                                    )}

                                    {formValues.userInfo.servicesReceivedConsent && formValues.userInfo.servicesReceived?.length > 0 && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Services Currently Received</p>
                                            <p className='text-base text-white'>
                                                {(formValues.userInfo.servicesReceived as string[])
                                                    .map((s) => servicesReceivedOptions.find((opt) => opt.value === s)?.text || s)
                                                    .join(', ')}
                                            </p>
                                        </div>
                                    )}

                                    {/* Main Concerns (Step 3) */}
                                    {formValues.userInfo.mainConcerns &&
                                        formValues.userInfo.mainConcerns.length > 0 && (
                                            <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                                <p className='mb-1 text-sm font-medium text-white'>Main Concerns</p>
                                                <p className='text-base text-white'>
                                                    {formValues.userInfo.mainConcerns
                                                        ?.map((concern) => {
                                                            if (concern === 'other') {
                                                                return formValues.userInfo.mainConcernsOther || 'Other'
                                                            }
                                                            return mainConcernOptions.find(
                                                                (opt) => opt.value === concern
                                                            )?.text
                                                        })
                                                        .join(', ')}
                                                </p>
                                            </div>
                                        )}

                                    {/* Journey Stage (Step 4) */}
                                    {formValues.userInfo.currentDiagnoseStep && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Journey Stage</p>
                                            <p className='text-lg text-white'>
                                                {formValues.userInfo.currentDiagnoseStep === 'pre-diagnosis-waitlist'
                                                    ? 'Currently on the waitlist for an evaluation'
                                                    : currentDiagnoseStepOptions.find(
                                                          (opt) => opt.value === formValues.userInfo.currentDiagnoseStep
                                                      )?.text || formValues.userInfo.currentDiagnoseStep}
                                            </p>
                                        </div>
                                    )}

                                    {formValues.userInfo.currentDiagnoseStep === 'pre-diagnosis-waitlist' &&
                                        formValues.userInfo.waitlistSignUpDate && (
                                            <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                                <p className='mb-1 text-sm font-medium text-white/85'>
                                                    Waitlist Sign-up
                                                </p>
                                                <p className='text-base text-white'>
                                                    {formValues.userInfo.waitlistSignUpDate}
                                                </p>
                                            </div>
                                        )}

                                    {/* Diagnosis Info (Step 5, conditional) */}
                                    {formValues.userInfo.diagnosisInformation?.length > 0 && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white/80'>Diagnosis</p>
                                            <p className='text-base text-white'>
                                                {(formValues.userInfo.diagnosisInformation as string[]).map((val) =>
                                                    val === 'other'
                                                        ? formValues.userInfo.diagnosisInformationOther || 'Other'
                                                        : diagnosisOptions.find((opt) => opt.value === val)?.text || val
                                                ).join(', ')}
                                            </p>
                                        </div>
                                    )}

                                    {/* Communication Level (Step 6) */}
                                    {formValues.userInfo.communicationLevel && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Communication Level</p>
                                            <p className='text-base text-white'>
                                                {communicationLevelOptions.find(
                                                    (opt) => opt.value === formValues.userInfo.communicationLevel
                                                )?.text || formValues.userInfo.communicationLevel}
                                            </p>
                                        </div>
                                    )}

                                    {/* Support Needs (Step 7) */}
                                    {formValues.userInfo.supportNeeds && (
                                        <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                            <p className='mb-1 text-sm font-medium text-white'>Daily Support Needs</p>
                                            <p className='text-base text-white'>
                                                {supportNeedsOptions.find(
                                                    (opt) => opt.value === formValues.userInfo.supportNeeds
                                                )?.text || formValues.userInfo.supportNeeds}
                                            </p>
                                        </div>
                                    )}

                                    {/* Goals for Child (Step 8) */}
                                    {formValues.userInfo.userGoalsForChild &&
                                        formValues.userInfo.userGoalsForChild.length > 0 && (
                                            <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                                <p className='mb-1 text-sm font-medium text-white'>Your Goals(child)</p>
                                                <p className='text-base text-white'>
                                                    {formValues.userInfo.userGoalsForChild
                                                        ?.map((goal) => {
                                                            if (goal === 'other') {
                                                                return (
                                                                    formValues.userInfo.userGoalsForChildOther ||
                                                                    'Other'
                                                                )
                                                            }
                                                            const goalLabels = userGoalsForChildOptions.reduce(
                                                                (acc, goal) => {
                                                                    acc[goal.value] = goal.text
                                                                    return acc
                                                                },
                                                                {}
                                                            )

                                                            return goalLabels[goal] || goal
                                                        })
                                                        .join(', ')}
                                                </p>
                                            </div>
                                        )}

                                    {/* Goals for Caregiver (Step 9) */}
                                    {formValues.userInfo.userGoalsCareGiver &&
                                        formValues.userInfo.userGoalsCareGiver.length > 0 && (
                                            <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                                <p className='mb-1 text-sm font-medium text-white'>
                                                    Your Goals(caregiver)
                                                </p>
                                                <p className='text-base text-white'>
                                                    {formValues.userInfo.userGoalsCareGiver
                                                        ?.map((goal) => {
                                                            if (goal === 'other') {
                                                                return formValues.userInfo.userGoalsCareGiver || 'Other'
                                                            }
                                                            const goalLabels = userGoalsForChildOptions.reduce(
                                                                (acc, goal) => {
                                                                    acc[goal.value] = goal.text
                                                                    return acc
                                                                },
                                                                {}
                                                            )

                                                            return goalLabels[goal] || goal
                                                        })
                                                        .join(', ')}
                                                </p>
                                            </div>
                                        )}

                                    {/* Previous Activities (not in onboarding flow, shown last) */}
                                    {formValues.userInfo.previousActivities &&
                                        formValues.userInfo.previousActivities.length > 0 && (
                                            <div className='p-3 rounded-lg bg-white/10  border border-white/20 shadow-sm'>
                                                <p className='mb-1 text-sm font-medium text-white'>
                                                    Previous Activities
                                                </p>
                                                <p className='text-base text-white'>
                                                    {formValues.userInfo.previousActivities
                                                        ?.map((activity) => {
                                                            if (activity === 'other') {
                                                                return (
                                                                    formValues.userInfo.previousActivitiesOther ||
                                                                    'Other'
                                                                )
                                                            }

                                                            const activityLabels = previousActivitiesOptions.reduce(
                                                                (acc, activity) => {
                                                                    acc[activity.value] = activity.text
                                                                    return acc
                                                                },
                                                                {}
                                                            )
                                                            return activityLabels[activity] || activity
                                                        })
                                                        .join(', ')}
                                                </p>
                                            </div>
                                        )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </section>
    )
}

